// Booking, waitlisting and cancellation rules.
//
// Pure decisions only: every function takes the current state and returns what
// SHOULD happen. Persisting the result is the caller's job, which keeps the
// awkward parts (late-cancel fees, waitlist promotion) unit-testable.

import { checkEntitlement } from './entitlements.js';

export const BOOKING_STATUS = {
  BOOKED: 'booked',
  WAITLISTED: 'waitlisted',
  CANCELLED: 'cancelled',
  LATE_CANCELLED: 'late_cancelled',
  ATTENDED: 'attended',
  NO_SHOW: 'no_show',
};

export const REFUSE = {
  CLOSED: 'booking_closed',
  NOT_OPEN_YET: 'not_open_yet',
  ALREADY_BOOKED: 'already_booked',
  FULL_NO_WAITLIST: 'full_no_waitlist',
  CANCELLED_CLASS: 'class_cancelled',
  ALREADY_RESOLVED: 'already_resolved',
};

const REFUSE_MESSAGE = {
  [REFUSE.CLOSED]: 'Booking has closed for this class.',
  [REFUSE.NOT_OPEN_YET]: 'Booking has not opened yet.',
  [REFUSE.ALREADY_BOOKED]: 'Already booked into this class.',
  [REFUSE.FULL_NO_WAITLIST]: 'Class is full and the waitlist is off.',
  [REFUSE.CANCELLED_CLASS]: 'This class was cancelled.',
  [REFUSE.ALREADY_RESOLVED]: 'This booking has already been settled.',
};

/** Statuses a booking can still be cancelled out of. */
const LIVE_STATUSES = ['booked', 'waitlisted'];

const MIN = 60000;

/**
 * policy: {
 *   opensHoursBefore: 336,      // 14 days
 *   closesMinutesBefore: 0,     // 0 = right up to start time
 *   waitlist: true,
 *   waitlistCap: 10,
 *   lateCancelMinutes: 720,     // inside 12h is a late cancel
 *   lateCancelFeeCents: 1500,
 *   noShowFeeCents: 2000,
 *   lateCancelReturnsCredit: false
 * }
 */
export const DEFAULT_POLICY = {
  opensHoursBefore: 336,
  closesMinutesBefore: 0,
  waitlist: true,
  waitlistCap: 10,
  lateCancelMinutes: 720,
  lateCancelFeeCents: 0,
  noShowFeeCents: 0,
  lateCancelReturnsCredit: false,
};

function refuse(code, detail) {
  return { ok: false, code, reason: REFUSE_MESSAGE[code], ...detail };
}

export function bookingWindow(occurrence, policy = DEFAULT_POLICY) {
  const start = new Date(occurrence.startsAt).getTime();
  return {
    opensAt: new Date(start - policy.opensHoursBefore * 60 * MIN).toISOString(),
    closesAt: new Date(start - policy.closesMinutesBefore * MIN).toISOString(),
  };
}

/** Statuses that occupy a place in the room. */
export const OCCUPIES_SEAT = [BOOKING_STATUS.BOOKED, BOOKING_STATUS.ATTENDED];

/**
 * How full a class is.
 *
 * ATTENDED counts. It did not, and the consequence was that checking someone
 * in freed their seat: a two-person class with both members checked in
 * reported nought booked and accepted a third. The front desk checks people in
 * as they walk through the door, so every arrival opened a phantom slot and a
 * busy class quietly overbooked itself all morning.
 */
export function seatCounts(bookings) {
  const booked = bookings.filter((b) => OCCUPIES_SEAT.includes(b.status)).length;
  const waitlisted = bookings.filter((b) => b.status === BOOKING_STATUS.WAITLISTED).length;
  return { booked, waitlisted };
}

/**
 * Decide what happens when a member tries to book.
 * Returns { ok, action: 'book'|'waitlist', position?, entitlement } or a refusal.
 */
export function requestBooking({
  occurrence, bookings = [], membership, plan, memberId,
  now = new Date().toISOString(), policy = DEFAULT_POLICY,
}) {
  if (occurrence.cancelled) return refuse(REFUSE.CANCELLED_CLASS);

  const existing = bookings.find(
    (b) => b.memberId === memberId
      && [BOOKING_STATUS.BOOKED, BOOKING_STATUS.WAITLISTED].includes(b.status),
  );
  if (existing) return refuse(REFUSE.ALREADY_BOOKED, { status: existing.status });

  const { opensAt, closesAt } = bookingWindow(occurrence, policy);
  if (now < opensAt) return refuse(REFUSE.NOT_OPEN_YET, { opensAt });
  if (now > closesAt) return refuse(REFUSE.CLOSED, { closesAt });

  const entitlement = checkEntitlement({
    membership, plan, occurrence, dateKey: occurrence.date,
  });
  if (!entitlement.ok) return { ok: false, ...entitlement };

  const { booked, waitlisted } = seatCounts(bookings);
  if (booked < occurrence.capacity) {
    return { ok: true, action: 'book', entitlement };
  }

  if (!policy.waitlist) return refuse(REFUSE.FULL_NO_WAITLIST);
  if (waitlisted >= policy.waitlistCap) {
    return refuse(REFUSE.FULL_NO_WAITLIST, { waitlistCap: policy.waitlistCap });
  }
  // A waitlist seat does not consume a credit — that happens on promotion.
  return { ok: true, action: 'waitlist', position: waitlisted + 1, entitlement };
}

/** Is a cancellation inside the late window? */
export function isLateCancel(occurrence, now, policy = DEFAULT_POLICY) {
  const cutoff = new Date(occurrence.startsAt).getTime() - policy.lateCancelMinutes * MIN;
  return new Date(now).getTime() > cutoff;
}

/**
 * Decide the effects of a cancellation: status, whether a credit comes back,
 * what fee applies, and who gets promoted off the waitlist.
 */
export function cancelBooking({
  occurrence, bookings = [], bookingId,
  now = new Date().toISOString(), policy = DEFAULT_POLICY,
}) {
  const booking = bookings.find((b) => b.id === bookingId);
  if (!booking) return { ok: false, code: 'not_found', reason: 'Booking not found.' };

  // Cancelling is only meaningful on a live seat. consumedCredit records what
  // the seat COST and is deliberately never cleared, so without this guard a
  // second cancel still reports returnsCredit and the caller refunds again —
  // a 6-credit pack goes 6, 5, 6, 7 and keeps climbing. The same guard stops a
  // no-show or an attended seat being reversed into a refund after the fact.
  if (!LIVE_STATUSES.includes(booking.status)) {
    return refuse(REFUSE.ALREADY_RESOLVED, { status: booking.status });
  }

  const wasBooked = booking.status === BOOKING_STATUS.BOOKED;
  const late = wasBooked && isLateCancel(occurrence, now, policy);

  const effects = {
    ok: true,
    bookingId,
    status: late ? BOOKING_STATUS.LATE_CANCELLED : BOOKING_STATUS.CANCELLED,
    feeCents: late ? policy.lateCancelFeeCents : 0,
    // Waitlisted seats never consumed a credit, so there is nothing to return.
    returnsCredit: booking.consumedCredit === true && (!late || policy.lateCancelReturnsCredit),
    promoted: null,
  };

  // Freeing a real seat pulls the first person off the waitlist.
  if (wasBooked) {
    const next = bookings
      .filter((b) => b.status === BOOKING_STATUS.WAITLISTED)
      .sort((a, b) => (a.waitlistPosition ?? 0) - (b.waitlistPosition ?? 0))[0];
    if (next) {
      effects.promoted = {
        bookingId: next.id,
        memberId: next.memberId,
        status: BOOKING_STATUS.BOOKED,
        consumesCredit: true,
      };
    }
  }

  return effects;
}

/**
 * Reindex waitlist positions after any change, so they stay 1..n PER CLASS.
 *
 * Grouped by occurrence deliberately. Callers hold one flat list of every
 * booking in the gym, and renumbering that list as a whole produces positions
 * like A#1, A#3, B#2 — each class's waitlist silently interleaved with every
 * other one. Grouping here makes the function correct regardless of what the
 * caller hands it.
 */
export function reindexWaitlist(bookings) {
  const groups = new Map();
  for (const b of bookings) {
    if (b.status !== BOOKING_STATUS.WAITLISTED) continue;
    const key = b.occurrenceId ?? '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(b);
  }

  const positions = new Map();
  for (const list of groups.values()) {
    list
      .slice()
      .sort((a, b) => (a.waitlistPosition ?? 0) - (b.waitlistPosition ?? 0))
      .forEach((b, i) => positions.set(b.id, i + 1));
  }

  return bookings.map((b) => (positions.has(b.id)
    ? { ...b, waitlistPosition: positions.get(b.id) }
    : b));
}

/** Mark unresolved bookings after a class has finished. */
export function settleAttendance({ bookings, policy = DEFAULT_POLICY }) {
  return bookings.map((b) => {
    if (b.status !== BOOKING_STATUS.BOOKED) return b;
    if (b.checkedInAt) return { ...b, status: BOOKING_STATUS.ATTENDED };
    return { ...b, status: BOOKING_STATUS.NO_SHOW, feeCents: policy.noShowFeeCents };
  });
}
